import type { ColumnOrderState, ColumnPinningState, VisibilityState } from '@tanstack/react-table';
import { useRef, useState } from 'react';

import type { TableStateControl } from '../../hooks';
import type { FilterGroupItem, FilterShortcut, FilterType, SavedFilter } from '../../utils/filterSettings';
import { AdvancedFilterDialog } from '../AdvancedFilterDialog';
import { Button, IconButton } from '../Button';
import { Icon } from '../Icon';
import { ManageColumnsDialog } from '../ManageColumnsDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SearchButtonInput } from '../SearchButtonInput';
import { SplitButton, type SplitButtonAction } from '../SplitButton';
import { SvgIcon } from '../SvgIcon';
import type { TableColumn } from '../Table';
import { type Density, type ExportScope, TableMenu, type TableMenuAction } from '../TableMenu';
import { TablePagination } from '../TablePagination';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { AppliedFiltersRow, type CustomFilterPills } from './AppliedFiltersRow';

export type ToolbarShortcut = {
  label: React.ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
  isActive: boolean;
  testId: string;
};

export type TableActionRowProps<TData> = {
  // Table configuration
  columns: TableColumn<TData>[];
  filterTypes: FilterType[];

  // State control (pagination, filtering, search)
  stateControl: TableStateControl;
  totalItems: number;
  /** Selected row count; shows "x of y selected" next to the pagination range when above zero. */
  selectedCount?: number;
  isLoading: boolean;

  // Local UI state (not in URL)
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState) => void;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: (pinning: ColumnPinningState) => void;
  density: Density;
  onDensityChange: (density: Density) => void;

  // Action callbacks
  onMyFilters?: () => void;
  onMyFiltersMouseEnter?: () => void;
  myFiltersLabel?: React.ReactNode;
  isMyFiltersActive?: boolean;
  /** When provided, shows a Bookmarks button that filters to bookmarked items (e.g. ProjectsTable). */
  onBookmarks?: () => void;
  onBookmarksMouseEnter?: () => void;
  isBookmarksActive?: boolean;
  onExportVisible?: () => void;
  onExportAll?: () => void;
  /** Opt-in: also expose the export actions as a split button in the left toolbar cluster, after the filter button. */
  showExportSplitButton?: boolean;
  /** Export in progress; the matching menu button spins, the split button spins for either. */
  exporting?: ExportScope;
  /** Extra settings-menu actions, shown after the export buttons. */
  menuActions?: TableMenuAction[];
  onResetView?: () => void;
  canResetView?: boolean;

  // Filter shortcuts (inside the filter dialog)
  filterShortcuts?: FilterShortcut[];

  // Saved filters (user-defined, inside the filter dialog)
  savedFilters?: SavedFilter[];
  onSaveFilter?: (name: string, filter: FilterGroupItem) => void;
  onRemoveSavedFilter?: (id: string) => void;

  // Toolbar shortcut buttons rendered next to "My Assignments"
  toolbarShortcuts?: ToolbarShortcut[];

  /** Custom controls (e.g. a dropdown) rendered at the end of the left toolbar cluster. */
  toolbarContent?: React.ReactNode;

  /** Custom filter pills (e.g. "Map filtering Table") shown alongside regular filters. */
  customFilterPills?: CustomFilterPills;
  /** Show the search box. Defaults to true. */
  enableSearch?: boolean;
  /** Show the filter button and applied-filter pills. Defaults to true. */
  enableFilters?: boolean;
  /** Show the settings menu: density, page size, export and reset view. Defaults to true. */
  enableSettings?: boolean;
  /** Let users hide and show columns. Defaults to true. */
  enableHiding?: boolean;
  /** Let users pin columns, and drag them between pinned groups in the columns dialog. Defaults to true. */
  enableColumnPinning?: boolean;
  /** Let users reorder columns by dragging. Defaults to true. */
  enableColumnOrdering?: boolean;
  /** Show the pager and the page-size setting. Defaults to true. */
  enablePagination?: boolean;
};

/**
 * Table Action Row, optimized for DRF
 */
function TableActionRow<TData>({
  columns,
  filterTypes,
  stateControl,
  totalItems,
  selectedCount = 0,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnPinning,
  onColumnPinningChange,
  density,
  onDensityChange,
  onMyFilters,
  onMyFiltersMouseEnter,
  myFiltersLabel,
  isMyFiltersActive = false,
  onBookmarks,
  onBookmarksMouseEnter,
  isBookmarksActive = false,
  onExportVisible,
  onExportAll,
  showExportSplitButton = false,
  exporting,
  menuActions,
  onResetView,
  canResetView = true,
  isLoading = false,
  filterShortcuts,
  savedFilters,
  onSaveFilter,
  onRemoveSavedFilter,
  toolbarShortcuts,
  toolbarContent,
  customFilterPills,
  enableSearch = true,
  enableFilters = true,
  enableSettings = true,
  enableHiding = true,
  enableColumnPinning = true,
  enableColumnOrdering = true,
  enablePagination = true,
}: TableActionRowProps<TData>) {
  // The columns dialog hides and reorders columns; pinning alone happens from the column headers.
  const enableColumnEditing = enableHiding || enableColumnOrdering;
  const [isColumnsMenuOpen, setIsColumnsMenuOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const addFiltersRef = useRef<HTMLButtonElement>(null);
  const [isTableMenuOpen, setIsTableMenuOpen] = useState(false);

  // Destructure stateControl
  const { pagination, setPageSize, currentPage, setCurrentPage, globalFilter, setGlobalFilter, filters, setFilters } =
    stateControl;

  const exportActions: SplitButtonAction[] = [
    ...(onExportVisible
      ? [
          {
            value: 'export-visible',
            label: 'Export Visible',
            description: 'All rows that match the current filters and search, in the current sort order.',
            onClick: onExportVisible,
            testId: 'advanced-table-export-visible',
          },
        ]
      : []),
    ...(onExportAll
      ? [
          {
            value: 'export-all',
            label: 'Export All',
            description: 'All rows, ignoring filters and search, in the current sort order.',
            onClick: onExportAll,
            testId: 'advanced-table-export-all',
          },
        ]
      : []),
  ];

  const hasToolbar =
    enableSearch ||
    enableSettings ||
    enableColumnEditing ||
    enableFilters ||
    enablePagination ||
    (showExportSplitButton && exportActions.length > 0) ||
    onMyFilters !== undefined ||
    onBookmarks !== undefined ||
    (toolbarShortcuts?.length ?? 0) > 0 ||
    toolbarContent != null ||
    selectedCount > 0;

  // Handler to remove a specific filter by index
  const handleRemoveFilter = (index: number) => {
    const newFilters = {
      ...filters,
      items: filters.items.filter((_, i) => i !== index),
    };
    setFilters(newFilters);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        {hasToolbar && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {enableSearch && (
                <SearchButtonInput
                  value={globalFilter}
                  onChange={setGlobalFilter}
                  className="h-6"
                  data-testid="advanced-table-search"
                />
              )}
              {enableSettings && (
                <Popover open={isTableMenuOpen} onOpenChange={setIsTableMenuOpen}>
                  <PopoverTrigger asChild>
                    <Tooltip text="Settings" asChild>
                      <IconButton
                        icon="gear"
                        variant={isTableMenuOpen ? 'selected' : 'secondary'}
                        size="icon"
                        aria-label="Table settings"
                        data-testid="advanced-table-settings"
                      />
                    </Tooltip>
                  </PopoverTrigger>
                  <PopoverContent align="start">
                    <TableMenu
                      itemsPerPage={pagination.pageSize}
                      onItemsPerPageChange={enablePagination ? setPageSize : undefined}
                      density={density}
                      onDensityChange={onDensityChange}
                      onExportVisible={onExportVisible}
                      onExportAll={onExportAll}
                      exporting={exporting}
                      actions={menuActions}
                      onResetView={onResetView}
                      canResetView={canResetView}
                    />
                  </PopoverContent>
                </Popover>
              )}
              {enableColumnEditing && (
                <Tooltip text="Edit columns" asChild>
                  <IconButton
                    icon="column"
                    variant="secondary"
                    size="icon"
                    aria-label="Edit columns"
                    aria-haspopup="dialog"
                    aria-expanded={isColumnsMenuOpen}
                    onClick={() => setIsColumnsMenuOpen(true)}
                    data-testid="advanced-table-edit-columns"
                  />
                </Tooltip>
              )}
              {enableFilters && (
                <Tooltip text="Add filters" asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label="Add filters"
                    aria-haspopup="dialog"
                    aria-expanded={isFilterDialogOpen}
                    ref={addFiltersRef}
                    onClick={() => setIsFilterDialogOpen(true)}
                    data-testid="advanced-table-add-filters"
                  >
                    <SvgIcon icon="filter" size={14} />
                  </Button>
                </Tooltip>
              )}
              {showExportSplitButton && exportActions.length > 0 && (
                <SplitButton
                  actions={exportActions}
                  loading={exporting !== undefined}
                  buttonClassName="h-6 py-0.5"
                  data-testid="advanced-table-export-split-button"
                />
              )}
              {onMyFilters && (
                <Button
                  variant={isMyFiltersActive ? 'selected' : 'secondary'}
                  aria-pressed={isMyFiltersActive}
                  onClick={onMyFilters}
                  onMouseEnter={onMyFiltersMouseEnter}
                  className="h-6 py-0.5 px-2"
                  data-testid="advanced-table-my-filters"
                >
                  {myFiltersLabel}
                </Button>
              )}
              {toolbarShortcuts?.map((shortcut) => (
                <Button
                  key={shortcut.testId}
                  variant={shortcut.isActive ? 'selected' : 'secondary'}
                  aria-pressed={shortcut.isActive}
                  onClick={shortcut.onClick}
                  onMouseEnter={shortcut.onMouseEnter}
                  className="h-6 py-0.5 px-2"
                  data-testid={shortcut.testId}
                >
                  {shortcut.label}
                </Button>
              ))}
              {onBookmarks && (
                <Button
                  variant={isBookmarksActive ? 'selected' : 'secondary'}
                  aria-pressed={isBookmarksActive}
                  onClick={onBookmarks}
                  onMouseEnter={onBookmarksMouseEnter}
                  className="h-6 py-0.5 px-2"
                  data-testid="advanced-table-bookmarks"
                >
                  <Icon icon="bookmarkFilled" size={14} /> <Text className="text-xs">Bookmarks</Text>
                </Button>
              )}
              {toolbarContent}
            </div>
            <div className="flex items-center gap-2">
              {selectedCount > 0 && (
                <Text className="text-2xs text-blue-60" data-testid="advanced-table-selected-count">
                  {`${selectedCount.toLocaleString()} of ${totalItems.toLocaleString()} selected`}
                </Text>
              )}
              {enablePagination && (
                <TablePagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  pageSize={pagination.pageSize}
                  onPageChange={setCurrentPage}
                  isLoading={isLoading}
                  data-testid="advanced-table-pagination"
                  prevTestId="advanced-table-page-prev"
                  nextTestId="advanced-table-page-next"
                />
              )}
            </div>
          </div>
        )}

        {(enableFilters || (customFilterPills?.length ?? 0) > 0) && (
          <AppliedFiltersRow
            filters={filters}
            filterTypes={filterTypes}
            handleRemoveFilter={handleRemoveFilter}
            customFilterPills={customFilterPills}
            emptyFocusRef={addFiltersRef}
          />
        )}
      </div>

      <ManageColumnsDialog
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        columnPinning={columnPinning}
        onColumnPinningChange={onColumnPinningChange}
        enableHiding={enableHiding}
        enableColumnPinning={enableColumnPinning}
        enableColumnOrdering={enableColumnOrdering}
        open={isColumnsMenuOpen}
        onOpenChange={setIsColumnsMenuOpen}
      />
      <AdvancedFilterDialog
        open={isFilterDialogOpen}
        onOpenChange={setIsFilterDialogOpen}
        filters={filters}
        onFiltersChange={setFilters}
        filterTypes={filterTypes}
        filterShortcuts={filterShortcuts}
        savedFilters={savedFilters}
        onSaveFilter={onSaveFilter}
        onRemoveSavedFilter={onRemoveSavedFilter}
      />
    </>
  );
}

export { TableActionRow };
